(ns hai.sharing.components
  (:require
   [clojure.string :as str]
   [hai-model.model :as model]
   [hai.contacts.helpers :as contacts]
   [hai.entity.helpers :as entity]
   [hai.sharing.helper :as sharing]
   [hai.sharing.people :as people]
   [hai.users.form :as users.form]
   [hai.users.helper :as users]
   [hai.ui.components :as c]
   [hai.ui.components.card :as card]
   [hai.ui.components.collection.bulk-actions :as bulk-actions]
   [hai.ui.components.collection.table :as collection]
   [hai.ui.components.collection.views.cards :as cards]
   [hai.ui.components.collection.views.table :as table]
   [hai.ui.components.form.core :as form]
   [hai.ui.components.form.input :as input]
   [hai.ui.components.form.select.components :as select.components]
   [hai.ui.components.form.select.multi-select :as multi-select]
   [hai.ui.components.form.select.searchable-multi-select :as searchable-multi-select]
   [hai.ui.components.form.select.single-select :as single-select]
   [hai.ui.components.new-design.button :as button]
   [hai.ui.components.pill :as pill-ui]
   [hai.ui.icons :as icons]
   [hai.ui.rf-sugar :refer [<s >e >e! defeffect defevent-fx defsub]]
   [humbleai.belt.dom :as dom]
   [humbleai.belt.malli.transform.default-value :as default-value]
   [humbleai.belt.malli.type.domain :as type.domain]
   [humbleai.belt.malli.type.email :as type.email]
   [lambdaisland.ornament :as o]
   [medley.core :refer [assoc-some]]))

(def ^:private share-icon (icons/mui-icon :outlined :person_add))

(defn sharing-dialog-title
  []
  [c/DialogTitle {:style {:display "flex" :align-items "center" :gap "4px"}}
   [share-icon {:style {:width "24px" :height "24px"}}]
   "Sharing"])

(defn extract-scopes
  [s]
  (when (string? s)
    (loop [s s
           res []]
      (let [email-match (first (re-find type.email/email-regex s))
            domain-match (first (re-find type.domain/domain-regex s))
            {:keys [postfix result]} (cond
                                       ;; email and domain will both match in two scenarios;
                                       ;; firstly, if the next logical match is an email the email
                                       ;; will match both and secondly, if the next logical match is
                                       ;; a domain and there is a further email match the domain
                                       ;; match will be correct but the email match will be present
                                       ;; but logically incorrect. We differentiate the two by
                                       ;; checking if the current domain is included in the current
                                       ;; email.
                                       (and email-match
                                            (str/includes? email-match domain-match))
                                       (let [index (str/index-of s email-match)
                                             prefix (-> s
                                                        (subs 0 index)
                                                        (str/replace #"[,<>\"]" "")
                                                        (str/replace #"\s+" " ")
                                                        (str/trim))
                                             postfix (subs s (+ index (count email-match)))
                                             ;; A typed-in email is a contact scope carrying only the
                                             ;; email. The platform resolves the  recipient by email.
                                             contact (assoc-some
                                                      {:contact/email [email-match]}
                                                      :contact/full-name (not-empty prefix))]
                                         {:postfix postfix
                                          :result contact})

                                       domain-match
                                       (let [index (str/index-of s domain-match)
                                             ;; Scope match discards the prefix
                                             postfix (subs s (+ index (count domain-match)))
                                             domain {:entity/scope domain-match}]
                                         {:postfix postfix
                                          :result domain}))]
        (if (or email-match domain-match)
          (recur postfix (conj res result))
          (not-empty res))))))

(defn ->label
  [?contact-or-scope]
  (cond
    (or (:contact/uuid ?contact-or-scope)
        (:contact/email ?contact-or-scope))
    (or (not-empty (:contact/full-name ?contact-or-scope))
        (first (:contact/email ?contact-or-scope)))

    :else
    (:entity/scope ?contact-or-scope)))

(defn drop-nil-full-name
  "A contact picked from the dropdown carries whatever the query returned,
   including `:contact/full-name nil` for a nameless contact. The `:scope`
   schema's `:contact/full-name` is optional-but-non-nilable (`{:optional true}
   :string`), so a present-but-nil name fails coercion the moment the pick
   becomes the field value. Dropping the key (rather than nilling it) leaves the
   contact valid; `->share` / `->label` already fall back to the email."
  [selected]
  (mapv (fn [scope]
          (cond-> scope
            (nil? (:contact/full-name scope))
            (dissoc :contact/full-name)))
        selected))

(defn ->share
  "Scopes reaching `share/create` are domain scopes or contacts -- picked from
   the dropdown (carrying `:contact/uuid`) or typed-in emails. Keep a contact's
   identity, emails, and (when a `Name <email>` was typed) name -- never a
   fabricated uuid; the platform resolves the recipient from the email and names
   the contact it adds to the sharer's book. `not-empty` guards against sending a
   present-but-nil name, which would fail coercion."
  [scope]
  (if (:entity/scope scope)
    scope
    (assoc-some (select-keys scope [:contact/uuid :contact/email])
                :contact/full-name (not-empty (:contact/full-name scope)))))

(defn contact-disabled-reason
  "Sharing resolves the recipient by email, so an email-less contact can't be
   shared with. Rather than hide it, the dropdown shows it disabled with this
   reason as a tooltip — telling the user *why* it can't be picked. Returns nil
   (i.e. selectable) for anything that isn't an email-less contact row."
  [contact]
  (when (and (:contact/uuid contact)
             (empty? (:contact/email contact)))
    "This contact has no email"))

(defn- ->entity-identity
  ;; TODO I'm not sure about this, at the very least it wasn't intentional, I hope to investigate at
  ;; some point
  "Extracts only the identity attribute from an entity for use in mutations.
   The share mutations expect just the identity map, not the full entity."
  [entity]
  (select-keys entity [(model/->uuid-attr entity)]))

(o/defstyled user-pill pill-ui/removable-pill
  [:&.highlighted {:outline "1px solid red"}]
  ([{:as pill
     :form.field.collection/keys [remove-self!]
     :form.field/keys [current-value]}]
   [:<>
    {:class (cond-> []
              @(<s [:multi-select.pill/highlighted? pill])
              (conj "highlighted"))}
    ;; TODO need a user or scope ->label function that exists in a component in CLJC
    [:span (->label current-value)]
    [pill-ui/pill-close
     {:on-click (fn [_]
                  (remove-self!)
                  (>e [:select/toggle-option pill current-value]))}]]))

(defn extract-scopes-from-select
  [db ?select]
  (let [search-string (form/->field-current-value db ?select :table/search)]
    (extract-scopes search-string)))

(defevent-fx ::process-input
  [{:keys [db]} ?select]
  (let [{:select/keys [active-option] :as form} (multi-select/->form db ?select)]
    (if (not-empty (:table/rows (:form/current-value form)))
      ;; A disabled (email-less) active option can't be picked — swallow the key.
      (when-not (contact-disabled-reason active-option)
        {:dispatch [:select/toggle-active-option ?select]})
      (if-let [extracted-scopes (extract-scopes-from-select db ?select)]
        {:dispatch [:select/toggle-options ?select extracted-scopes]}
        {}))))

(defevent-fx ::process-space
  [{:keys [db]} ?select]
  ;; Space must NOT confirm a highlighted contact — it's an ordinary character
  ;; while typing a name ("John Smith"). It still commits a fully typed email or
  ;; domain, so several recipients can be entered space-separated.
  (when-let [extracted-scopes (extract-scopes-from-select db ?select)]
    {:dispatch [:select/toggle-options ?select extracted-scopes]}))

(defevent-fx ::process-tab
  [{:keys [db]} ?select e]
  (let [{:select/keys [active-option] :as form} (multi-select/->form db ?select)]
    (if (not-empty (:table/rows (:form/current-value form)))
      ;; A disabled (email-less) active option can't be picked — let Tab fall
      ;; through to its default (move focus) instead of selecting.
      (when-not (contact-disabled-reason active-option)
        (dom/stop e)
        {:dispatch [:select/toggle-active-option ?select]})
      (let [search (-> form :form/current-value :table/search)]
        (if (not-empty search)
          (if-let [scopes (extract-scopes search)]
            (do
              (dom/stop e)
              {:dispatch [:select/toggle-options ?select scopes]})
            {})
          {})))))

(defevent-fx ::process-backspace
  [{:keys [db]} ?select]
  (let [{:multi-select/keys [highlighted-selected]
         {:table/keys [search]} :form/current-value} (multi-select/->form db ?select)]
    (when-not search
      (if highlighted-selected
        {:dispatch [:multi-select/remove-highlighted ?select]}
        {:dispatch [:select/highlight-last-selected ?select]}))))

(defn- search-input-render
  [input]
  [input/auto-expanding input
   [input/no-styling-input input]])

(o/defstyled search-field :div
  :flex :items-center
  :h-100%
  ([select]
   [:<>
    [select.components/options-radix select]
    [multi-select/select-input**
     ;; TODO this should be part of :style
     (update-in select
                [:form/fields :table/search]
                assoc
                ;; `searchable-multi-select` already puts a caller-supplied
                ;; `:placeholder` on this field; only default it when the caller
                ;; didn't set one, so referrals can word it their own way.
                :form.field/placeholder (or (get-in select [:form/fields
                                                            :table/search
                                                            :form.field/placeholder])
                                            "Add emails to invite")
                :input/render {:input.render/input search-input-render}
                ;; Regains focus after each commit remounts the (uncontrolled) input -- see
                ;; `input/field` -- so the user can keep typing the next contact without re-clicking.
                :auto-focus? true
                :on-key-down
                (fn [e]
                  (case (.-key e)
                    "Enter" (>e! [::process-input select])
                    " " (>e! [::process-space select])
                    "Backspace" (>e! [::process-backspace select])
                    "Tab" (>e! [::process-tab select e])
                    "ArrowDown" (>e! [:select/set-active-option-to select :next])
                    "ArrowUp" (>e! [:select/set-active-option-to select :previous])
                    nil)))]]))

(defeffect ::on-submit
  [{:form/keys [on-submit] :as ?form}]
  (on-submit))

(defevent-fx ::on-submit
  [_ ?form]
  {::on-submit ?form})

(defevent-fx ::on-share
  [{:keys [db]} ?form]
  (let [scopes-form-id [(form/->id ?form) :scopes]]
    (if-let [extracted-scopes (extract-scopes-from-select db scopes-form-id)]
      ;; Merge with whatever's already been committed as pills (via Enter/Tab) rather than
      ;; replacing it -- otherwise clicking Share with leftover uncommitted text discards any
      ;; scopes already added.
      (let [existing-scopes (form/->field-current-value db ?form :scopes)
            merged-scopes (into (vec existing-scopes) extracted-scopes)]
        {:dispatch-n [[:form.field/set-value! ?form :scopes merged-scopes]
                      [:form.field/set-value! scopes-form-id :table/rows []]
                      [:form.field/clear! scopes-form-id :table/search]
                      [::on-submit ?form]]})
      {::on-submit ?form})))

(defsub ::valid?
  [db ?form]
  (let [form (form/->form db ?form)
        scopes-form-id [(form/->id ?form) :scopes]
        extracted-scopes (extract-scopes-from-select db scopes-form-id)
        scopes (:scopes (:form/current-value form))]
    (or (not-empty scopes)
        (not-empty extracted-scopes))))

(o/defstyled share-entity-render :div
  card/frame
  [:.scopes :flex :flex-wrap {:gap "0.5rem"} :w-100%]
  [:.permission :flex :items-center :items-baseline card/container-gap :ml-auto]
  ([{:as form
     :form/keys [on-submit valid?]
     {:keys [scopes permission]} :form/fields}]
   [:<>
    [:div.scopes
     [form/collection (assoc scopes :form.field.collection/field user-pill)]
     [searchable-multi-select/field
      (assoc scopes
             :query [{:contacts/table [{:collection/items contacts/query}]}]
             :transform-selected drop-nil-full-name
             :render {:style select.components/radix
                      :label contacts/->label+email
                      :disabled-reason contact-disabled-reason
                      :field search-field})]]
    [:div.permission
     [single-select/field-slim permission]
     ;; TODO this should just be a `:disabled?` flag
     (if @(<s [::valid? form])
       [button/dark-small
        {:on-click (fn [_] (>e [::on-share form]))
         :text "Share"}]
       [button/dark-small-disabled
        {:text "Share"}])]]))

;; One share holds one permission, and each is a role on its own: a viewer
;; reads, a contributor reads and edits, an admin also shares.
(def ^:private permission-options
  [{:value :permission.type/read
    :title "Viewer"}
   {:value :permission.type/update
    :title "Contributor"}
   {:value :permission.type/admin
    :title "Admin"}])

(o/defstyled share-entity :div
  ([table entity on-open]
   [:<>
    (let [form-id [::add-user-form (model/->ident entity)]]
      [form/component
       {:schema [:map
                 [:scopes
                  {:on-change (fn [_]
                                (>e [:form.field/clear! [form-id :scopes] :table/search]))}
                  [:vector :scope]]
                 [:permission
                  {:form.field/placeholder "Permission"
                   :form.field.select/options permission-options}
                  :any]]
        :initial-value {:permission :permission.type/read}
        :id form-id
        :render share-entity-render
        :on-submit (fn [{:keys [permission scopes]}]
                     (>e [:resolve (into []
                                         ;; share is called scope upt to this point
                                         (map (fn [share]
                                                (list 'share/create
                                                      {:entity (->entity-identity entity)
                                                       :share (->share share)
                                                       :permission permission})))
                                         scopes)
                          {:on-success
                           (fn [_]
                             ;; TODO select reset?
                             (>e [:form.field/clear! [form-id :scopes] :table/search])
                             (>e [:form.field/set-value! [form-id :scopes] :multi-select/selected []])
                             (>e [:table/resolve table]))}]))}])]))

;; A permission-free variant of the share box — the same contact search and
;; typed-email scopes, but no role dropdown (co-owners are always admin) and an
;; "Add co-owner" button. Reused by the spaces detail page.
(o/defstyled co-owner-add-render :div
  card/frame
  [:.scopes :flex :flex-wrap {:gap "0.5rem"} :w-100%]
  [:.actions :flex :items-center :ml-auto]
  ([{:as form {:keys [scopes]} :form/fields}]
   [:<>
    [:div.scopes
     [form/collection (assoc scopes :form.field.collection/field user-pill)]
     [searchable-multi-select/field
      (assoc scopes
             :query [{:contacts/table [{:collection/items contacts/query}]}]
             :transform-selected drop-nil-full-name
             :render {:style select.components/radix
                      :label contacts/->label+email
                      :disabled-reason contact-disabled-reason
                      :field search-field})]]
    [:div.actions
     (if @(<s [::valid? form])
       [button/dark-small
        {:on-click (fn [_] (>e [::on-share form]))
         :text "Add co-owner"}]
       [button/dark-small-disabled
        {:text "Add co-owner"}])]]))

(defn co-owner-add
  "Contact-searching add box for co-owners: the same scope search the People box
   (`share-entity`) uses — pick from your contacts or type an email — minus the
   permission role, since a co-owner is always admin. `on-add` is called with the
   picked scopes as share maps (`->share`); the host folds them into
   `space/add-co-owner`. `id` namespaces the form state."
  [{:keys [id on-add]}]
  [form/component
   {:schema [:map
             [:scopes
              {:on-change (fn [_]
                            (>e [:form.field/clear! [id :scopes] :table/search]))}
              [:vector :scope]]]
    :id id
    :render co-owner-add-render
    :on-submit (fn [{:keys [scopes]}]
                 (when (seq scopes)
                   (on-add (mapv ->share scopes)))
                 (>e [:form.field/clear! [id :scopes] :table/search])
                 (>e [:form.field/set-value! [id :scopes] :multi-select/selected []]))}])

;; Permissions table

(o/defstyled controls collection/controls
  :flex :flex-col :items-baseline
  :w-100%
  [:.bulk-actions :ml-auto]
  ([table entity]
   [:<>
    [share-entity table entity]
    [:div.bulk-actions [bulk-actions/dropdown table]]]))

(def permissions-query
  [:entity/permissions
   :entity/scope
   :user/uuid
   :user/email
   :user/full-name])

;; The table's rows also say which one is the entity's explicit owner — the
;; batched `:entity/shared-with` (the same `permissions-query`) does not.
(def ^:private permissions-table-query
  (conj permissions-query :entity/owner?))

(defn permission-label
  [permission]
  (case permission
    ::people/owner "Owner"
    :permission.type/read "Viewer"
    :permission.type/update "Contributor"
    :permission.type/admin "Admin"
    (some-> permission name)))

(defn- permission-field
  "The Permission cell. The owner's row is a static label — ownership lives
   outside the share groups, so there is no permission to change — and every
   other row keeps the dropdown."
  [{:collection/keys [entity] :as field}]
  (if (:entity/owner? entity)
    [table/table-cell (permission-label people/owner)]
    [c/TableCell
     {:class ["table-input"]}
     [single-select/field field]]))

(defn permissions-table
  "The shared-with table for `entity`. `:read-only?` renders the same banded
   table for display only — no share form, no bulk actions, permissions shown as
   labels — for a surface whose adds/removes go through their own controls (e.g.
   a space's co-owners, managed by `space/add-co-owner`)."
  ([entity] (permissions-table entity nil))
  ([entity {:keys [read-only?]}]
   (let [ident (entity/->ident entity)
         id [::permissions-table ident]]
     [collection/component
      (cond-> {:id id
               :query [{ident [{:entity.shared-with/table [{:collection/items permissions-table-query}]}]}]
               :paginate? true
      ;; TODO boolean malli transformer
               :editable? (and (not read-only?) (boolean (sharing/can-share? entity)))
      ;; TODO editable forms without update-mutation
               :update-mutation 'not/used
               :on-change (fn [rows]
                            (let [entity-id (->entity-identity entity)
                                  mutations (into []
                                                  (comp (filter (comp :form/dirty? :form meta))
                                                        (map (fn [{::keys [permission] :as share}]
                                                               (if (= ::remove permission)
                                                                 (list 'share/delete
                                                                       {:entity entity-id
                                                                        :share (sharing/->share-ref share)})
                                                                 (list 'share/update-to
                                                                       {:entity entity-id
                                                                        :share (sharing/->share-ref share)
                                                                        :permission permission})))))
                                                  rows)]
                              (>e [:resolve mutations
                                   {:on-success-fx [[:dispatch [:table/resolve id]]]}])))
               :schema [:map
                        [::permission
                         {:options (conj permission-options {:value ::remove
                                                             :title "Remove"})
                 ;; The owner row holds no permission: it carries `people/owner`,
                 ;; a value the dropdown never offers, so it validates as a row
                 ;; while `permission-field` renders it as a label.
                          ::default-value/fn (fn [{:entity/keys [permissions owner?]}]
                                               (if owner? people/owner (people/share-permission permissions)))}
                         [:enum :permission.type/read :permission.type/update :permission.type/admin people/owner ::remove]]]
      ;; TODO default card render
               :card (fn [collection item]
                       [card/card
                        [cards/card-select-checkbox collection item]
                        [cards/column-render collection item]])
               :columns [{:header "Email / domain"
                 ;; TODO ...
                 ;; :attr ::user-scope
                 ;; :filter (filters/text-search {:placeholder "Email..."})
                          :render (fn [scoped-permissions]
                                    (if (:user/uuid scoped-permissions)
                                      [table/title-render
                                       {:title    (users/->label-with-email scoped-permissions)
                                        :on-click (fn [_]
                                                    (>e [:routing/navigate [:users/edit scoped-permissions]]))}]
                                      [table/table-cell
                                       (case (:entity/scope scoped-permissions)
                                         "*" "Everyone"
                                         "published" "Published"
                                         "public" "Public"
                                         (:entity/scope scoped-permissions))]))}
                         {:header "Permission"
                          :field  permission-field
                          :render (fn [{::keys [permission]}]
                                    [table/table-cell (permission-label permission)])
                          :attr   ::permission
                 ;; `::permission` is a synthetic front-end attr (see the schema's
                 ;; `::default-value/fn`), not a server-sortable field. Sorting on it
                 ;; sends an invalid query that errors in pathom, so opt out -- the
                 ;; `:attr` still drives the editable field, just not the sort.
                          :sort?  false
                          :width  "9em"}]}
        (not read-only?)
        (assoc
         :controls (fn [table] [controls table entity])
         :bulk-actions [{:title       "Remove access"
                         ;; The owner has no access to remove.
                         :disabled?   :entity/owner?
                         :mutation-fn (fn [selected]
                                        (let [entity-id (->entity-identity entity)]
                                          (into []
                                                (map (fn [share]
                                                       (list 'share/delete
                                                             {:entity entity-id
                                                              :share  (sharing/->share-ref share)})))
                                                selected)))
                         :alert       {:content (fn [{:table/keys [selected] :as table} action]
                                                  [bulk-actions/confirm-dialog
                                                   {:table  table
                                                    :action action}
                                                   [bulk-actions/title "Confirm Deletion of Organisations or Teams"]
                                                   [bulk-actions/description "Are you sure you want to delete the following Organisations or Teams?"]
                                                   (into [:ul]
                                                         (map (fn [{:group/keys [display-name]}]
                                                                [:li [:strong display-name]]))
                                                         selected)])}}]))])))

(defn dialog
  "Sharing dialog for any entity. `!open?` controls it; closing calls
   `on-closed` so the host can re-resolve and surface freshly-added
   collaborators. `description` names the entity — \"People with access to
   this task\"."
  [{:keys [entity description !open? on-closed]}]
  [c/DialogRoot
   {:open @!open?
    :onOpenChange (fn [opening?]
                    (reset! !open? opening?)
                    (when-not opening?
                      (on-closed)))}
   [c/DialogContent
    [sharing-dialog-title]
    [c/dialog-close-x]
    [c/DialogDescription description]
    [permissions-table entity]]])

;; Entity table components

(def shared-with-query
  {:entity/shared-with
   permissions-query})

(defn shared-with-label
  [{:entity/keys [scope] :as entity}]
  (cond
    (= "*" scope) "Everyone"
    (= "published" scope) "Published"
    scope (str "*@" scope)
    :else (users/->label entity)))

(defn shared-with-render
  [collection {:entity/keys [shared-with] :as entity}]
  (when (or shared-with
            (sharing/can-share? entity))
    [table/modal-cell
     {:text     (table/label
                 {:items shared-with
                  :xform (map shared-with-label)
                  :text  "Add +"})
      :on-close (fn [] (>e [:table/resolve collection]))}
     [sharing-dialog-title]
     [c/DialogDescription "Users and groups with access to resource"]
     [permissions-table entity]]))

(def shared-with-column
  {:header                 "Shared With"
   :attr                   :entity/shared-with
   :sort?                  false
   :render-with-collection shared-with-render
   :filter                 {:schema [:vector :string]
                            :type   :filter/multi-select
                            :field  users.form/user-search-emails-field}})

